import { Text } from '@/clients/shared-components/Typography';
import { ENV_VARS } from '@/clients/util/env';
import { postJson } from '@/clients/util/xhr';
import { SamlLookupJsonResponse, SAMLLookupRequestBody } from '@/ifaces/user';
import { useSignUp, useSignIn } from '@clerk/clerk-react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { ProfAIAvatar } from '../components/ProfAIAvatar';
import styles from './login-styles.module.css';

export function SAMLLoginPage() {
  const { signUp } = useSignUp();
  const { signIn } = useSignIn();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState('');

  const { mutate: submitSection, isPending: isUpdating } = useMutation({
    mutationFn: async () => samlLookupJson({ email }),
    onSuccess: async (resp: SamlLookupJsonResponse) => {
      if (resp.status && resp.status === 200 && resp.body?.responseType) {
        const redirectUrlComplete = ENV_VARS.VITE_HOST_REDIRECT;

        if (resp.body.responseType === 'saml') {
          await signUp!.authenticateWithRedirect({
            strategy: 'enterprise_sso', // Clerk SAML
            emailAddress: email,
            redirectUrl: '/corp-callback',
            redirectUrlComplete, // HomePage
          });
          setSuccess(`Authenticating...`);
        } else if (resp.body.responseType === 'email') {
          try {
            await signIn!.create({
              strategy: 'email_link',
              identifier: email,
              redirectUrl: redirectUrlComplete,
            });
          } catch (err: unknown) {
            const errorMsg = `There was an error with your account login. Please retry or contact support.`;
            setError(errorMsg);
            return;
          }
          setSuccess(`Section Account Confirmed! Please Check Your Email For A Login Link.`);
        } else {
          const errorMsg = `There was an error with your account login. Please retry or contact support.`;
          setError(errorMsg);
        }
      } else {
        const errorMsg = `There was an error with your account login. Please retry or contact support.`;
        setError(errorMsg);
      }
    },
    onError: (err) => {
      setError(
        err instanceof Error
          ? err.message
          : `There was an error with your account login. Please retry or contact support.`,
      );
    },
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);
    submitSection();
  };

  const handleEmailUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const emailInput = e.target.value.toLocaleLowerCase();
    setEmail(emailInput);
  };

  return (
    <div className={styles['login-page']}>
      <div className={styles['login-box-container']}>
        <div className={`${styles['half-login-page']} ${styles['half-login-left']}`}>
          <img
            alt={`ProfAI Login`}
            src="/img/loginAbstract.png"
            width={'100%'}
            style={{ borderRadius: '10px 0 0 10px', float: 'left' }}
          />
        </div>
        <div className={styles['half-login-page']}>
          <div className={styles['invite-form']}>
            <div className={styles['invite-avatar-holder']}>
              <ProfAIAvatar size={80} />
            </div>

            <Text header l bold style={{ marginBottom: '10px' }}>
              <span
                className={styles['invite-title-text']}
                style={{ color: 'var(--colors-tomato)' }}
              >
                {' '}
                Welcome to ProfAI!
              </span>
            </Text>

            <Text header xs bold>
              <span className={styles['invite-sub-text']}>
                Enter your work or Section related email address below to begin.
              </span>
            </Text>

            <div className={styles['invite-result-text']}>
              {isUpdating && <div className={styles['loading-message']}>Loading...</div>}
              {error && <div className={styles['error-message']}>{error}</div>}
              {success && <div className={styles['success-message']}>{success}</div>}
            </div>

            <div className={styles['invite-form-left-panel']}>
              <form onSubmit={handleSubmit}>
                <div className={styles['form-group']}>
                  <label htmlFor="email">Email:</label>
                  <input
                    id="email"
                    type="text"
                    value={email}
                    onChange={(e) => handleEmailUpdate(e)}
                    className={`${styles['app-input']} ${styles['form-input']}`}
                    required
                  />
                </div>
                <div className={styles['form-actions']} style={{ margin: '40px 0 0px' }}>
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className={`${styles['save-button']} ${styles['neo']} ${styles['large']}`}
                  >
                    {isUpdating ? `Accessing login...` : `Login`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

async function samlLookupJson(body: SAMLLookupRequestBody): Promise<SamlLookupJsonResponse> {
  const response = await postJson(`api/user/samlLookup`, body, false);
  const json = await response.json();
  return {
    status: response.status,
    body: json,
  };
}
